import React, { useMemo } from 'react';

import currencyFormatter from 'currency-formatter';

import { Box, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import type { CreditReversalOrder } from '~/services/OrdersService';

import type { CashPayment } from './types';
import { getCashPayments } from './utils';

const CASH_PAYMENT_COLUMNS: Array<GridColDef<CashPayment>> = [
  { field: 'id_payment', headerName: 'Payment ID', width: 280 },
  { field: 'type', headerName: 'Type', width: 120 },
  {
    field: 'ext_transaction_id',
    headerName: 'External Payment ID',
    width: 280,
    valueFormatter: (value: string | null) => value ?? '—',
  },
  {
    field: 'amount',
    headerName: 'Amount',
    width: 140,
    valueFormatter: (value: number, row: CashPayment) =>
      row.currency
        ? `${currencyFormatter.format(value ?? 0, { code: row.currency, precision: 2 })}`
        : String(value ?? '—'),
  },
  { field: 'currency', headerName: 'Currency', width: 90 },
  { field: 'intent', headerName: 'Intent', width: 90 },
  { field: 'status', headerName: 'Status', width: 110 },
];

interface Props {
  row: CreditReversalOrder;
}

export default function CashPaymentsDetailPanel({ row }: Props) {
  const cashPayments = useMemo(() => getCashPayments(row.payments), [row.payments]);

  return (
    <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Cash payments
      </Typography>
      <DataGrid
        rows={cashPayments}
        density="compact"
        columns={CASH_PAYMENT_COLUMNS}
        getRowId={(r) => r.id_payment}
        localeText={{ noRowsLabel: 'No cash payments for this order.' }}
        disableColumnFilter
        disableColumnMenu
        hideFooter
      />
    </Box>
  );
}
